import { useQuery } from '@apollo/client';
import { AddCircle, Bolt, Sell } from '@mui/icons-material';
import { Box, Button } from '@mui/material';
import type { GridRowSelectionModel } from '@mui/x-data-grid-pro';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { FilterSidebar, FilterToggle } from '~/components/Filter';
import { ListPagination } from '~/components/ListPagination';
import {
  ListActions,
  ListContainer,
  ListContent,
  ListHeader,
  ListPageContainer,
} from '~/components/PageLayout';
import { RouterBreadcrumbs } from '~/components/RouterBreadcrumbs';
import { Count } from '~/components/count';
import { SearchBar } from '~/components/forms/search-bar';
import { ButtonLink } from '~/components/link';
import { Toolbar } from '~/components/toolbar';
import { useAppContext } from '~/contexts';
import type { DeviceSignalsOrderBy } from '~/generated/graphql';
import { useOrderByParams } from '~/hooks/order-by';
import { usePaginationParams, useTotalCount } from '~/hooks/pagination';
import { NetworkDeviceSignalsDocument } from '../queries/list.generated';
import {
  BulkActions,
  SignalFilterAccordion,
  SignalFilterChips,
  Table,
  TagsDialog,
} from './components';
import { BulkTagDialog } from './components/BulkTagDialog';
import { SignalFiltersProvider, useSignalFiltersParams, useSignalFiltersReducer } from './context';

export const DeviceSignalList = () => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search')?.trim() ?? '';

  const handleSearch = useCallback(
    (value: string) =>
      setSearchParams((params) => {
        value ? params.set('search', value) : params.delete('search');
        params.set('page', '1');
        return params;
      }),
    [setSearchParams],
  );

  const filterParams = useSignalFiltersParams();

  const { orderBy } = useOrderByParams<DeviceSignalsOrderBy>();
  const { handleOutOfBounds, page, perPage } = usePaginationParams();

  const filtersActive = useMemo(
    () => Object.values(filterParams).some((v) => v.length > 0),
    [filterParams],
  );

  const { data, loading } = useQuery(NetworkDeviceSignalsDocument, {
    variables: {
      condition: filterParams,
      networkId: currentNetwork.id,
      page,
      perPage,
      search,
      ...(orderBy && { orderBy }),
    },
    onCompleted: (data) => handleOutOfBounds(data.network?.deviceSignals.pageInfo),
  });

  const totalCount = useTotalCount(data?.network?.deviceSignals.pageInfo.nodesCount);
  const deviceSignals = data?.network?.deviceSignals.nodes ?? [];

  const eventTypeNames = useMemo(
    () => data?.network?.allEventTypes.map((et) => et.name),
    [data?.network?.allEventTypes],
  );

  const tagNames = useMemo(
    () => data?.network?.deviceSignalTags.map((t) => t.name),
    [data?.network?.deviceSignalTags],
  );

  const [signalFiltersState, signalFiltersDispatch] = useSignalFiltersReducer(
    filterParams,
    eventTypeNames,
    tagNames,
  );

  const [selectedIds, setSelectedIds] = useState<GridRowSelectionModel>([]);
  const [tagSignalDialogOpen, setTagSignalDialogOpen] = useState(false);
  const [showManageTags, setManageTags] = useState(false);

  useEffect(() => setSelectedIds([]), [setSelectedIds]);

  const canDestroySelectedSignals = selectedIds.every((selectedId) => {
    const signal = deviceSignals.find((signal) => signal.id === selectedId);
    return signal?.canDestroy.value;
  });

  return (
    <SignalFiltersProvider
      {...signalFiltersState}
      clearFilters={() => signalFiltersDispatch({ type: 'CLEAR_FILTERS' })}
      reinitialize={(params) => signalFiltersDispatch({ type: 'REINITIALIZE', params })}
      setFilter={(value) => signalFiltersDispatch({ type: 'SET_FILTER', ...value })}
      toggleFilter={(field) => signalFiltersDispatch({ type: 'TOGGLE_FILTER', field })}
      togglePanel={() => signalFiltersDispatch({ type: 'TOGGLE_PANEL' })}
    >
      <Helmet title={t('pages.device_signals.title')} />
      <Toolbar
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setManageTags(!showManageTags)}
              startIcon={<Sell />}
              variant="outlined"
            >
              {t('common.tags.manage_tags')}
            </Button>
            <ButtonLink color="primary" startIcon={<AddCircle />} variant="contained" to="new">
              {t('pages.device_signals.buttons.new')}
            </ButtonLink>
          </Box>
        }
        breadcrumbsLabel={<RouterBreadcrumbs />}
        titleIcon={<Bolt />}
        titleText={t('pages.device_signals.title')}
      />
      <ListPageContainer>
        <FilterSidebar open={signalFiltersState.panel.open} persistAs="deviceSignals:filterWidth">
          <SignalFilterAccordion filterCounts={data?.network?.deviceSignalFilterInfo} />
        </FilterSidebar>
        <ListContainer>
          <ListHeader>
            <ListActions>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterToggle
                  active={filtersActive}
                  onToggle={() => signalFiltersDispatch({ type: 'TOGGLE_PANEL' })}
                />
                <SearchBar
                  onChange={handleSearch}
                  placeholder={t('pages.device_signals.search_placeholder')}
                  search={search}
                />
              </Box>
              <Box sx={{ display: { md: 'flex', xs: 'none' }, alignItems: 'center' }}>
                {selectedIds.length > 0 ? (
                  <BulkActions
                    canDestroySelectedSignals={canDestroySelectedSignals}
                    onTagSignal={() => setTagSignalDialogOpen(true)}
                    selectedIds={selectedIds}
                  />
                ) : null}
                <Count
                  selectedCount={selectedIds.length}
                  totalCount={totalCount}
                  thing="common.pluralize.signal_plural"
                />
              </Box>
            </ListActions>
            <SignalFilterChips />
          </ListHeader>
          <ListContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                <Table
                  deviceSignals={deviceSignals}
                  loading={loading}
                  onCheck={setSelectedIds}
                  selectedIds={selectedIds}
                />
              </Box>
              <Box sx={{ flexShrink: 0 }}>
                <ListPagination totalCount={totalCount} />
              </Box>
            </Box>
          </ListContent>
        </ListContainer>
      </ListPageContainer>

      {showManageTags && <TagsDialog onClose={() => setManageTags(false)} open />}

      {tagSignalDialogOpen && (
        <BulkTagDialog
          signalIds={selectedIds as string[]}
          signalTags={tagNames ?? []}
          onClose={() => setTagSignalDialogOpen(false)}
          onComplete={() => setSelectedIds(() => [])}
          open
          tags={filterParams.tags}
        />
      )}
    </SignalFiltersProvider>
  );
};
